import React, { useEffect } from 'react';
import { Modal, Spin, Alert, Button, Input, Space, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useCreateShare } from './hooks/useCreateShare';
import { logger } from '@src/utils/misc/logger';
import { getCustomerInfo } from '@src/globalStore/profileStore';

const { baseUrl } = window.conviva;

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
}

export const ShareModal: React.FC<ShareModalProps> = ({ open, onClose, sessionId }) => {
  const { createShare, shareId, loading, error } = useCreateShare();

  useEffect(() => {
    if (open && sessionId) {
      createShare(sessionId);
    }
  }, [open, sessionId]);

  // Build share URL with customer switch prefix for auto-switching to correct customer
  const getShareUrl = () => {
    if (!shareId) return '';

    const { customerId } = getCustomerInfo();
    const nexaSharePath = `${baseUrl}/nx/share/${shareId}`;

    // Use customer_switch prefix to ensure user is switched to correct customer
    return `${window.location.origin}/customer_switch/${customerId}/?next=${nexaSharePath}`;
  };

  const shareUrl = getShareUrl();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      message.success('Copied to clipboard');
    } catch (err) {
      logger.error('Failed to copy link:', err);
      message.error('Failed to copy link');
    }
  };

  // Auto-copy link when ready
  useEffect(() => {
    if (shareId && !loading) {
      handleCopy();
    }
  }, [shareId, loading]);

  return (
    <Modal title="Share" open={open} onCancel={onClose} footer={null} width={600}>
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, fontSize: 16 }}>Generating share link...</div>
        </div>
      )}

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {shareId && !loading && (
        <Space style={{ width: '100%', backgroundColor: 'whitesmoke' }}>
          <a target="_blank" rel="noopener noreferrer" href={shareUrl}>
            {shareUrl}
          </a>

          <Button type="primary" icon={<CopyOutlined />} onClick={handleCopy} />
        </Space>
      )}
    </Modal>
  );
};
